import { Section } from "@/components/layout/section/Section";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardHeading,
  CardTitle
} from "@/components/ui/card";
import { ReorderButton } from "@/components/reorder";
import { REORDER_BUTTON_TONE_CLASS } from "@/components/reorder/ReorderButton";
import { Skeleton } from "@/components/ui/skeleton";
import {
  formatOrderDate,
  formatOrderNumber,
  getFulfillmentStatusBadge
} from "@/pages/orders/order-history-utils";
import { getOrderDisplayTotal } from "@/queries/orders/order-insights";
import {
  useCustomerOrders,
  useCustomerTopSupplier,
  type CustomerOrder,
  type TopSupplier
} from "@/queries/orders";
import { useSellersMap } from "@/queries/sellers";
import { useStockReorderPreviewQuery } from "@/queries/customer/stock-reorder";
import { useStockTracking } from "@/queries/customer/stock-tracking";
import { useStoreCurrencyCode } from "@/queries/regions";
import { StockReorderButton } from "@/components/stock-tracking/StockReorderButton";
import { ProductThumbnailImage } from "@/components/product/ProductThumbnailImage";
import { stockItemTitle, stockItemVariantLabel } from "@/lib/stock-item";
import { convertToLocale } from "@/utils/money";
import type {
  StoreStockReorderLine,
  StoreStockTrackingItem
} from "@repo/api-sdk";
import { Tooltip } from "@medusajs/ui";
import { TruncateTooltip } from "@repo/design-system";
import { cn } from "@repo/design-system/utils";
import { Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  Boxes,
  CheckCircle2,
  ChevronRight,
  ImageOff,
  Package,
  RefreshCw,
  Store
} from "lucide-react";

/**
 * Home "Highlights" region - a 3-up grid of personalized command-center cards
 * built honestly from real data (epic #70). Ships two cards today:
 *
 * - Recent Orders (storefront#74) - the salon's latest orders.
 * - Your Top Supplier (storefront#75) - the most-ordered supplier.
 *
 * - Due to Reorder (storefront#72) - opt-in tracked items at/below their reorder
 *   level (api#148). Display-only for now; one-tap reorder lands with api#152.
 */
export function Highlights() {
  return (
    <Section
      title="Highlights"
      description="Your orders and suppliers at a glance"
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <RecentOrdersCard />
        <TopSupplierCard />
        <DueToReorderCard />
      </div>
    </Section>
  );
}

// ---- Recent Orders (storefront#74) ----

/** How many recent orders to surface in the card. Exported so the home loader
 * can prefetch the exact same query (matching key). */
export const RECENT_ORDERS_LIMIT = 4;

function RecentOrdersCard() {
  const { data, isLoading, error } = useCustomerOrders({
    limit: RECENT_ORDERS_LIMIT
  });
  const orders = data?.orders ?? [];

  return (
    <Card className="h-full">
      <CardHeader>
        <CardHeading>
          <CardTitle className="flex items-center gap-2">
            <Package
              className="h-4 w-4 text-purple-600 dark:text-purple-400"
              aria-hidden="true"
            />
            Recent Orders
          </CardTitle>
        </CardHeading>
        <Button
          variant="ghost"
          size="sm"
          asChild
          className="text-purple-600 hover:text-purple-700 dark:text-purple-400"
        >
          <Link to="/orders">
            View all
            <ChevronRight className="ml-1 h-4 w-4" />
          </Link>
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading && <RecentOrdersSkeleton />}

        {error && !isLoading && (
          <p className="px-5 py-6 text-sm text-red-600 dark:text-red-400">
            Could not load recent orders.
          </p>
        )}

        {!isLoading && !error && orders.length === 0 && <RecentOrdersEmpty />}

        {!isLoading && !error && orders.length > 0 && (
          <ul className="divide-border divide-y" aria-label="Recent orders">
            {orders.map((order) => (
              <li key={order.id}>
                <RecentOrderRow order={order} />
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// Shared by both reorder actions in this card so they read as one control.
// A white, bordered square with a purple glyph sits quietly beside the row
// content; the tinted fill this replaces competed with the price and badges.
const REORDER_BUTTON_CLASS = `h-8 w-8 flex-shrink-0 ${REORDER_BUTTON_TONE_CLASS}`;

function RecentOrderRow({ order }: { order: CustomerOrder }) {
  const badge = getFulfillmentStatusBadge(order.fulfillment_status);
  const itemCount =
    order.items?.reduce((sum, item) => sum + (item.quantity ?? 0), 0) ?? 0;

  return (
    // The row opens that order's card on `/orders`, expanded (there is no
    // dedicated order-detail route yet). The reorder button sits beside that
    // link rather than inside it, since a button nested in an anchor is invalid
    // markup.
    <div className="flex items-center gap-3 px-5 py-3">
      <Link
        to="/orders"
        search={{ order: order.id }}
        aria-label={`View order ${formatOrderNumber(order)}`}
        className="-mx-2 flex min-w-0 flex-1 items-center gap-3 rounded-md px-2 py-1 transition-colors hover:bg-purple-50/40 dark:hover:bg-purple-900/10"
      >
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/40">
          <Package
            className="h-4 w-4 text-purple-600 dark:text-purple-400"
            aria-hidden="true"
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <TruncateTooltip
              as="span"
              className="text-sm font-semibold text-gray-900 dark:text-white"
            >
              {formatOrderNumber(order)}
            </TruncateTooltip>
            <span
              className={cn(
                "flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium",
                badge.className
              )}
            >
              {badge.label}
            </span>
          </div>
          <TruncateTooltip
            as="p"
            className="mt-0.5 text-xs text-gray-500 dark:text-gray-400"
          >
            {order.seller?.name && <>{order.seller.name} &middot; </>}
            {formatOrderDate(order.created_at)} &middot; {itemCount}{" "}
            {itemCount === 1 ? "item" : "items"}
          </TruncateTooltip>
        </div>
        <span className="flex-shrink-0 text-sm font-semibold text-gray-900 dark:text-white">
          {convertToLocale({
            amount: getOrderDisplayTotal(order),
            currency_code: order.currency_code
          })}
        </span>
      </Link>
      <ReorderButton
        orderId={order.id}
        seller={order.seller}
        currencyCode={order.currency_code}
        source="home_recent_orders"
        variant="ghost"
        size="icon"
        label="Reorder"
        icon={RefreshCw}
        iconOnly
        className={REORDER_BUTTON_CLASS}
      />
    </div>
  );
}

function RecentOrdersEmpty() {
  return (
    <div className="flex flex-col items-center justify-center px-5 py-8 text-center">
      <Package className="mb-3 h-8 w-8 text-gray-400" aria-hidden="true" />
      <p className="text-sm font-medium text-gray-900 dark:text-white">
        No orders yet
      </p>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Your recent orders will show up here.
      </p>
      <Button
        asChild
        size="sm"
        className="mt-3 bg-purple-600 text-white hover:bg-purple-700"
      >
        <Link to="/search">Browse products</Link>
      </Button>
    </div>
  );
}

function RecentOrdersSkeleton() {
  return (
    <div className="divide-border divide-y" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center gap-3 px-5 py-3">
          <Skeleton className="h-9 w-9 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3.5 w-32" />
            <Skeleton className="h-3 w-40" />
          </div>
          <Skeleton className="h-4 w-14" />
        </div>
      ))}
    </div>
  );
}

// ---- Your Top Supplier (storefront#75) ----

function TopSupplierCard() {
  const { data: top, isLoading, error } = useCustomerTopSupplier();
  // The sellers map gives the canonical display name; fall back to the name
  // carried on the order. (The seller type exposes no logo, so we render an
  // initial-letter avatar rather than fabricate one.)
  const { data: sellersMap } = useSellersMap();

  return (
    <Card className="h-full">
      <CardHeader>
        <CardHeading>
          <CardTitle className="flex items-center gap-2">
            <Store
              className="h-4 w-4 text-purple-600 dark:text-purple-400"
              aria-hidden="true"
            />
            Your Top Supplier
          </CardTitle>
        </CardHeading>
      </CardHeader>
      <CardContent>
        {isLoading && <TopSupplierSkeleton />}

        {error && !isLoading && (
          <p className="text-sm text-red-600 dark:text-red-400">
            Could not load your top supplier.
          </p>
        )}

        {!isLoading && !error && !top && <TopSupplierEmpty />}

        {!isLoading && !error && top && (
          <TopSupplierBody
            top={top}
            name={sellersMap?.[top.sellerId]?.name ?? top.name}
          />
        )}
      </CardContent>
    </Card>
  );
}

function TopSupplierBody({ top, name }: { top: TopSupplier; name: string }) {
  const initial = name.trim().charAt(0).toUpperCase() || "?";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <div
          className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-purple-100 text-lg font-bold text-purple-700 dark:bg-purple-900/40 dark:text-purple-300"
          aria-hidden="true"
        >
          {initial}
        </div>
        <div className="min-w-0">
          <TruncateTooltip
            as="p"
            className="text-base font-semibold text-gray-900 dark:text-white"
          >
            {name}
          </TruncateTooltip>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {top.orderCount} {top.orderCount === 1 ? "order" : "orders"}
            {top.approximate ? " (recent)" : ""} &middot; last on{" "}
            {formatOrderDate(top.lastOrderAt)}
          </p>
        </div>
      </div>
      <Button variant="outline" size="sm" asChild className="w-full">
        <Link to="/orders" search={{ supplier: top.sellerId }}>
          View their orders
        </Link>
      </Button>
    </div>
  );
}

function TopSupplierEmpty() {
  return (
    <div className="flex flex-col items-center justify-center py-6 text-center">
      <Store className="mb-3 h-8 w-8 text-gray-400" aria-hidden="true" />
      <p className="text-sm font-medium text-gray-900 dark:text-white">
        No top supplier yet
      </p>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Order to see your most-used supplier here.
      </p>
      <Button
        asChild
        size="sm"
        className="mt-3 bg-purple-600 text-white hover:bg-purple-700"
      >
        <Link to="/search">Browse products</Link>
      </Button>
    </div>
  );
}

function TopSupplierSkeleton() {
  return (
    <div className="flex flex-col gap-4" aria-hidden="true">
      <div className="flex items-center gap-3">
        <Skeleton className="h-12 w-12 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-3 w-36" />
        </div>
      </div>
      <Skeleton className="h-8 w-full" />
    </div>
  );
}

// ---- Due to Reorder (storefront#72) ----

/** How many due items to preview in the card before linking to the full list. */
export const DUE_PREVIEW_LIMIT = 4;

/**
 * Surfaces opt-in tracked items at/below their reorder level (api#148, via
 * `useStockTracking` - the same query the "My Stock" editor and hero stat use, so
 * the counts stay in sync) and lets the salon reorder each in one tap (api#152).
 * A lazy dry-run resolve (`useStockReorderPreviewQuery`) enriches the previewed
 * rows with the supplier + price they would reorder from, so the reorder is not a
 * black box; the per-row button drives the shared review-before-confirm flow.
 */
export function DueToReorderCard() {
  const { data, isLoading, error } = useStockTracking();

  const dueItems = (data?.stock_items ?? []).filter((item) => item.is_due);
  const dueCount = data?.due_count ?? 0;
  const totalTracked = data?.count ?? 0;
  const preview = dueItems.slice(0, DUE_PREVIEW_LIMIT);
  const hasMore = dueCount > preview.length;

  // Resolve supplier + price for the previewed rows only - one dry-run call,
  // keyed by those variant ids. The per-row Reorder button re-resolves on click.
  const previewVariantIds = preview.map((item) => item.variant_id);
  const reorderPreview = useStockReorderPreviewQuery(previewVariantIds);
  const storeCurrency = useStoreCurrencyCode();
  // Prices carry their own currency from the resolve; region is only the
  // fallback for the window before the preview resolves.
  const currencyCode =
    reorderPreview.data?.currency_code ?? storeCurrency ?? "AUD";
  const linesByVariant = new Map<string, StoreStockReorderLine>(
    (reorderPreview.data?.lines ?? []).map((line) => [
      line.variant_id ?? "",
      line
    ])
  );

  return (
    <Card className="h-full">
      <CardHeader>
        <CardHeading>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle
              className="h-4 w-4 text-amber-500"
              aria-hidden="true"
            />
            Due to Reorder
          </CardTitle>
        </CardHeading>
        {totalTracked > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="text-purple-600 hover:text-purple-700 dark:text-purple-400"
          >
            <Link to="/account/stock">
              My Stock
              <ChevronRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="p-0">
        {isLoading && <DueToReorderSkeleton />}

        {error && !isLoading && (
          <p className="px-5 py-6 text-sm text-red-600 dark:text-red-400">
            Could not load your stock.
          </p>
        )}

        {!isLoading && !error && totalTracked === 0 && (
          <DueToReorderNotTracking />
        )}

        {!isLoading && !error && totalTracked > 0 && dueCount === 0 && (
          <DueToReorderAllStocked />
        )}

        {!isLoading && !error && dueCount > 0 && (
          <>
            {preview.length > 0 && (
              <ul
                className="divide-border divide-y"
                aria-label="Items due to reorder"
              >
                {preview.map((item) => (
                  <li key={item.id}>
                    <DueItemRow
                      item={item}
                      line={linesByVariant.get(item.variant_id) ?? null}
                      resolving={reorderPreview.isLoading}
                      currencyCode={currencyCode}
                    />
                  </li>
                ))}
              </ul>
            )}
            {hasMore && (
              <div className="px-5 py-3">
                <Button variant="outline" size="sm" asChild className="w-full">
                  <Link to="/account/stock">
                    View all {dueCount} due to reorder
                  </Link>
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function DueItemRow({
  item,
  line,
  resolving,
  currencyCode
}: {
  item: StoreStockTrackingItem;
  /** The resolved reorder line for this variant (supplier + price), if fetched. */
  line: StoreStockReorderLine | null;
  /** Whether the supplier/price resolve is still in flight. */
  resolving: boolean;
  currencyCode: string;
}) {
  const productTitle = stockItemTitle(item);
  const variantLabel = stockItemVariantLabel(item);
  const thumbnail = item.variant?.product?.thumbnail ?? null;

  // No active offer resolved -> reorder is not possible; still show the row.
  const unavailable =
    line !== null && (line.status === "unavailable" || !line.seller);
  const resolvedPrice =
    line?.status === "repriced" ? line.new_price : line?.unit_price;
  const hasResolvedOffer =
    !!line && !unavailable && !!line.seller && resolvedPrice !== undefined;

  return (
    <div className="flex items-center gap-3 px-5 py-3">
      <Link
        to="/account/stock"
        aria-label={`Review ${productTitle} in My Stock`}
        className="-mx-2 flex min-w-0 flex-1 items-center gap-3 rounded-md px-2 py-1 transition-colors hover:bg-purple-50/40 dark:hover:bg-purple-900/10"
      >
        <div className="border-border flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-gray-50 dark:bg-neutral-900">
          <ProductThumbnailImage
            src={thumbnail}
            alt=""
            className="h-full w-full object-cover"
            fallback={
              <ImageOff
                className="h-4 w-4 text-gray-300 dark:text-gray-600"
                aria-hidden="true"
              />
            }
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <TruncateTooltip
              as="p"
              className="min-w-0 text-sm font-semibold text-gray-900 dark:text-white"
            >
              {productTitle}
            </TruncateTooltip>
            {/* Stock level sits on the title line, not as a floating pill, so the
                row keeps two lines and aligns with Recent Orders. */}
            <Tooltip content="On hand / reorder level" side="top">
              <span className="flex-shrink-0 text-xs font-semibold tabular-nums text-amber-700 dark:text-amber-400">
                {item.on_hand} / {item.reorder_level}
              </span>
            </Tooltip>
          </div>
          <TruncateTooltip
            as="p"
            className="mt-0.5 text-xs text-gray-500 dark:text-gray-400"
          >
            {variantLabel ? <>{variantLabel} &middot; </> : null}
            {hasResolvedOffer ? (
              <>
                {line.seller?.name} &middot;{" "}
                {convertToLocale({
                  amount: resolvedPrice,
                  currency_code: currencyCode
                })}
              </>
            ) : unavailable ? (
              <>no longer available</>
            ) : resolving ? (
              <>finding best price…</>
            ) : (
              <>reorder at {item.reorder_level}</>
            )}
          </TruncateTooltip>
        </div>
      </Link>
      <StockReorderButton
        variantIds={[item.variant_id]}
        items={[item]}
        source="due_item"
        size="icon"
        variant="ghost"
        label={unavailable ? "Unavailable" : "Reorder"}
        icon={<RefreshCw className="h-4 w-4" aria-hidden="true" />}
        iconOnly
        className={REORDER_BUTTON_CLASS}
        disabled={unavailable}
      />
    </div>
  );
}

function DueToReorderAllStocked() {
  return (
    <div className="flex flex-col items-center justify-center px-5 py-8 text-center">
      <CheckCircle2
        className="mb-3 h-8 w-8 text-green-500"
        aria-hidden="true"
      />
      <p className="text-sm font-medium text-gray-900 dark:text-white">
        You&apos;re all stocked up
      </p>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Nothing is at or below its reorder level.
      </p>
    </div>
  );
}

function DueToReorderNotTracking() {
  return (
    <div className="flex flex-col items-center justify-center px-5 py-8 text-center">
      <Boxes className="mb-3 h-8 w-8 text-gray-400" aria-hidden="true" />
      <p className="text-sm font-medium text-gray-900 dark:text-white">
        Track your stock
      </p>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Tell us what&apos;s on your shelf and we&apos;ll flag it when it runs
        low.
      </p>
      <Button
        asChild
        size="sm"
        className="mt-3 bg-purple-600 text-white hover:bg-purple-700"
      >
        <Link to="/account/stock">Set up My Stock</Link>
      </Button>
    </div>
  );
}

function DueToReorderSkeleton() {
  return (
    <div className="divide-border divide-y" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center gap-3 px-5 py-3">
          <Skeleton className="h-9 w-9 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3.5 w-32" />
            <Skeleton className="h-3 w-24" />
          </div>
          <Skeleton className="h-5 w-10 rounded-full" />
        </div>
      ))}
    </div>
  );
}
